import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { Autocomplete as MuiAutocomplete } from '@mui/material';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { PendingChangeKind } from 'constants/pendingChanges';
import { COLOR_PALETTE } from 'constants/ui';

import { ArrowCheckIcon, CancelIcon } from '../../icons';
import FieldWrapper from '../FieldWrapper';
import { FormCDSTestIds } from '../form.testIds';
import TextField from '../TextFieldCDS';

import { getAutocompleteStyles, indicatorStyles, listboxStyles } from './AutocompleteCDS.styles';
import {
  getOptionLabel as getOptionLabelUtil,
  scrollToSelectedOption,
  sortOptionsWithPins,
  withAriaLabel,
} from './AutocompleteCDS.utils';
import AutocompleteCDSOption from './AutocompleteCDSOption';
import AutocompleteCDSPopper from './AutocompleteCDSPopper';
import AutocompleteCDSTags from './AutocompleteCDSTags';

const disablePortalProp = {
  disablePortal: true,
};

const defaultOptions = [];
const defaultInputProps = {};

const getSlotProps = ({ onChange, onClose, selectedRef, multiple, name }) => ({
  popupIndicator: {
    disableRipple: true,
    sx: indicatorStyles,
  },
  clearIndicator: {
    sx: indicatorStyles,
    onClick: e => {
      onChange(e, multiple ? [] : null);
      onClose();
    },
    'data-fieldname': name,
  },
  listbox: {
    sx: listboxStyles,
    ref: ref => scrollToSelectedOption(ref, selectedRef.current),
  },
});

const AutocompleteCDS = ({
  disabled = false,
  options = defaultOptions,
  error = false,
  helperText = '',
  label = '',
  id,
  name,
  value = null,
  onChange,
  className = '',
  width = '100%',
  optionName = 'name',
  optionKey,
  optionClassName = '',
  autoFocus = false,
  required = false,
  OptionContent,
  inputProps = defaultInputProps,
  isLazyWrapped = false,
  disablePortal = false,
  dataTestId = '',
  dataTestIdOption = '',
  dataTestIdInput = '',
  ariaLabel = '',
  containerClassName = '',
  containerCustomStyles,
  getChipProps,
  withTooltip = false,
  columns = 1,
  customStyles,
  multiple = false,
  disableClearable = false,
  placeholder = '',
  isFieldChanged = false,
  fieldChangeKind = null,
  withPopupError = false,
  withSortedOptions = true,
  pinnedOptions,
  isIgnorePins = false,
  groupBy,
  withRequiredIcon = true,
  isOneRow = false,
  isHidden = false,
  getOptionLabel: getOptionLabelProp,
  renderInput: renderInputProp,
  ...restProps
}) => {
  const fieldId = id || name;

  const selectedRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const onOpen = useCallback(() => setIsOpen(true), []);

  const onClose = useCallback(() => {
    selectedRef.current = null;

    setIsOpen(false);
  }, []);

  const getOptionLabel = useCallback(
    option => getOptionLabelProp?.(option) ?? getOptionLabelUtil({ option, key: optionName }),
    [optionName, getOptionLabelProp],
  );

  const getOptionKey = useMemo(() => (optionKey ? option => option[optionKey] : null), [optionKey]);

  const formattedOptions = useMemo(
    () =>
      withSortedOptions
        ? sortOptionsWithPins(options, { getOptionLabel, groupBy, pinnedOptions, isIgnorePins })
        : options,
    [groupBy, getOptionLabel, options, withSortedOptions, pinnedOptions, isIgnorePins],
  );

  const renderDefaultInput = useCallback(
    params => {
      const { InputProps: extraInputProps, ...restInputProps } = inputProps || {};
      const mergedInputProps = extraInputProps
        ? { ...params.InputProps, ...extraInputProps }
        : params.InputProps;

      return (
        <TextField
          {...params}
          fullWidth
          noValidate
          autoFocus={autoFocus}
          className={className}
          customStyles={customStyles}
          data-testid={dataTestIdInput}
          error={error}
          fieldChangeKind={fieldChangeKind}
          helperText={helperText}
          isFieldChanged={isFieldChanged}
          label={label}
          margin="normal"
          multiple={multiple}
          name={name}
          placeholder={multiple && value?.length ? '' : placeholder || label}
          required={required}
          variant="outlined"
          withPopupError={withPopupError}
          withRequiredIcon={withRequiredIcon}
          {...restInputProps}
          InputProps={mergedInputProps}
          ariaLabel={ariaLabel}
        />
      );
    },
    [
      error,
      helperText,
      label,
      name,
      ariaLabel,
      className,
      dataTestIdInput,
      autoFocus,
      required,
      inputProps,
      customStyles,
      placeholder,
      multiple,
      value,
      isFieldChanged,
      fieldChangeKind,
      withPopupError,
      withRequiredIcon,
    ],
  );

  const renderResolvedInput = useCallback(
    params =>
      renderInputProp
        ? renderInputProp(withAriaLabel(params, ariaLabel))
        : renderDefaultInput(params),
    [renderInputProp, renderDefaultInput, ariaLabel],
  );

  const renderOption = useCallback(
    ({ key, ...restProps }, option, { selected }) => (
      <AutocompleteCDSOption
        OptionContent={OptionContent}
        dataTestId={dataTestIdOption}
        isLazyWrapped={isLazyWrapped}
        isSelected={selected}
        key={key}
        multiple={multiple}
        name={name}
        option={option}
        optionClassName={optionClassName}
        optionLabel={getOptionLabel(option)}
        optionProps={restProps}
        selectedRef={selectedRef}
      />
    ),
    [
      name,
      isLazyWrapped,
      dataTestIdOption,
      optionClassName,
      OptionContent,
      multiple,
      getOptionLabel,
    ],
  );

  const renderTags = useCallback(
    (tagValue, getTagProps) => (
      <AutocompleteCDSTags
        disablePortalProp={disablePortalProp}
        getChipProps={getChipProps}
        getOptionLabel={getOptionLabel}
        getTagProps={getTagProps}
        isOneRow={isOneRow}
        name={name}
        optionName={optionName}
        size={restProps.size}
        tagValue={tagValue}
        withTooltip={withTooltip}
      />
    ),
    [getChipProps, getOptionLabel, name, restProps.size, withTooltip, optionName, isOneRow],
  );

  const renderPopper = useCallback(
    ({ children, ...props }) => (
      <AutocompleteCDSPopper {...props} disablePortal={disablePortal}>
        {children}
      </AutocompleteCDSPopper>
    ),
    [disablePortal],
  );

  return (
    <FieldWrapper
      className={containerClassName}
      columns={columns}
      customStyles={containerCustomStyles}
      disablePortal={disablePortal}
      isHidden={isHidden}
      width={width}
    >
      <MuiAutocomplete
        id={fieldId}
        open={isOpen}
        sx={getAutocompleteStyles({ multiple, isOneRow, size: restProps.size })}
        onClose={onClose}
        onOpen={onOpen}
        {...restProps}
        clearOnBlur
        PopperComponent={renderPopper}
        classes={restProps.classes}
        clearIcon={
          <CancelIcon
            color={COLOR_PALETTE.GRAY[7]}
            data-fieldname={name}
            data-testid={FormCDSTestIds.getCloseIconId(name)}
            size={IconSize[16]}
          />
        }
        data-testid={dataTestId}
        disableClearable={disableClearable}
        disabled={disabled}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        groupBy={groupBy}
        multiple={multiple}
        options={formattedOptions}
        popupIcon={<ArrowCheckIcon size={IconSize[20]} />}
        renderInput={renderResolvedInput}
        renderOption={renderOption}
        renderTags={restProps.renderTags || renderTags}
        slotProps={getSlotProps({ onClose, onChange, selectedRef, multiple, name })}
        value={value}
        onChange={onChange}
      />
    </FieldWrapper>
  );
};

AutocompleteCDS.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
  OptionContent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  multiple: PropTypes.bool,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  dataTestIdInput: PropTypes.string,
  dataTestIdOption: PropTypes.string,
  dataTestId: PropTypes.string,
  disablePortal: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  listboxHeight: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  inputProps: PropTypes.object,
  label: PropTypes.string,
  optionClassName: PropTypes.string,
  optionName: PropTypes.string,
  optionKey: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.array]),
  getChipProps: PropTypes.func,
  renderInput: PropTypes.func,
  renderTags: PropTypes.func,
  options: PropTypes.array,
  isLazyWrapped: PropTypes.bool,
  containerClassName: PropTypes.string,
  withTooltip: PropTypes.bool,
  columns: PropTypes.number,
  width: PropTypes.string,
  customStyles: PropTypes.object,
  containerCustomStyles: PropTypes.object,
  placeholder: PropTypes.string,
  disableClearable: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  fieldChangeKind: PropTypes.oneOf(Object.values(PendingChangeKind)),
  isHidden: PropTypes.bool,
  withPopupError: PropTypes.bool,
  withRequiredIcon: PropTypes.bool,
  withSortedOptions: PropTypes.bool,
  pinnedOptions: PropTypes.shape({
    top: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    bottom: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  }),
  isIgnorePins: PropTypes.bool,
  isOneRow: PropTypes.bool,
  groupBy: PropTypes.func,
  getOptionLabel: PropTypes.func,
};

export default memo(AutocompleteCDS);
